import {ChangeDetectorRef, Component, ComponentFactoryResolver,} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {Customer} from "../../../model/customer/customer";
import {TranslateService} from '@ngx-translate/core';
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {FieldRecord} from "../../../model/api/field-record";
import {FieldRequest} from "../../../model/api/field-request";
import {CommonFieldService} from "../../../services/common/common-field.service";
import {CrudBaseComponent} from "../../../crystal-elements/crud/crud-base.component";
import {BankAccount} from "../../../model/customer/bank-account";
import {CCRoles} from "../../../model/security/cc-roles-type";
import {BrokerageTable} from "../../../model/customer/brokerage-table";
import {ExecTrader} from "../../../model/customer/exec-trader";
import {CustomerExecTraderService} from "../../../services/customer/customer-exec-trader.service";
import {ReportProfile} from "../../../model/archive/report-profile";
import TYPE = FormFieldConfig.TYPE;
import {AppConfigService} from "../../../services/app-config.service";
import {Router} from "@angular/router";
import {CrystalClearFrontUrl} from "../../../shared/pages/crystal-clear-front-url";

@Component({
  selector: 'app-exec-trader',
  templateUrl: './exec-trader.component.html',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
})
export class ExecTraderComponent extends CrudBaseComponent<ExecTrader> {

  countries: FieldRecord<any>[]

  languages: FieldRecord<any>[]

  owners: FieldRecord<any>[]

  currencies: FieldRecord<string>[]

  bankAccounts: FieldRecord<BankAccount>[] = [];
  brokerageTables: FieldRecord<BrokerageTable>[] = [];
  brokerageTablesVisibles: FieldRecord<BrokerageTable>[] = [];
  invoices: FieldRecord<ReportProfile>[] = [];

  constructor(cdref: ChangeDetectorRef,
              commonFieldService: CommonFieldService,
              entityService: CustomerExecTraderService,
              private appConfigService: AppConfigService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService,
              private router: Router) {
    super(cdref, messageService, confirmationService, translateService, commonFieldService);
    this.crudService = entityService;
    this.newRecord = new ExecTrader();
    this.newRecord.enabled = true;
    this.writeRoles = CCRoles.ROLES_CUSTOMER_WRITE;
  }

  ngOnInit() {
    super.ngOnInit();
    if(!this.appConfigService.billingEnabled)
      this.router.navigateByUrl(CrystalClearFrontUrl.HOME.path);
  }

  protected initConfigs() {
    super.initConfigs();

    this.tableConfig = [];
    if (!this.getIsReadOnly()) {
      this.tableConfig.push(
        {
          width: 30,
          type: 'BUTTON',
          notFilterable: true,
          notSortable: true,
          notResizable: true,
          class: value => 'centered',
          icon: value => value.enabled ? 'fas fa-toggle-on record-enabled' : 'fas fa-toggle-off record-disabled',
          command: (record: Customer) => {
            this.enableRecord(record);
          },
          mapId: value => '',
          hidden: this.getIsReadOnly()
        });
    }
    this.tableConfig.push(
      {
        field: 'owner',
        width: 100
      },
      {
        field: 'code',
        width: 130
      },
      {
        field: 'name',
        width: 150
      });
    this.tableConfig.push(
      {
        field: 'country',
        width: 100,
        icon: (value: Customer) => value.country ? 'flag-icon flag-icon-' + value.country.toLowerCase() : '',
        mapId: value => ''
      });
    if (this.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      this.tableConfig.push(
        {
          field: 'town',
          width: 100
        },
        {
          field: 'desk',
          width: 100
        },
        {
          field: 'email',
          width: 100
        },
        {
          field: 'address',
          width: 150
        },
        {
          field: 'brokerageTableName',
          width: 100
        }
      );
    }

    const groupMainFields: FormFieldConfig[] = [
      {
        field: 'ownerId',
        required: true,
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.owners,
        changed: event => {
          this.formRecord.ownerId = parseInt(event.value);
          this.formRecord.owner = this.findOptText(this.owners, event.value)
          this.refreshBrokerageTables(this.formRecord);
        }
      },
      {
        field: 'code',
        required: true,
        column: 4,
        type: TYPE.TEXT
      },
      {
        field: 'name',
        required: true,
        column: 5,
        type: TYPE.TEXT
      }];
    if (this.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      groupMainFields.push(
        {
          field: 'desk',
          column: 3,
          type: TYPE.TEXT
        });
    }
    groupMainFields.push(
      {
        field: 'country',
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        icon: id => id ? ('flag-icon flag-icon-' + id.toLowerCase()) : '',
        options: () => this.countries,
        changed: event => this.formRecord.country = event.value.toUpperCase()
      });

    if (this.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      groupMainFields.push(
        {
          field: 'email',
          column: 12,
          type: TYPE.TEXT
        },);
    }
    groupMainFields.push(
      {
        field: 'enabled',
        column: 3,
        type: TYPE.CHECKBOX
      });
    this.formConfig = [
      {
        group: '',
        fields: groupMainFields
      }];
    if (this.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      this.formConfig.push({
          group: 'address',
          fields:
            [{
              field: 'address',
              column: 12,
              type: TYPE.TEXT
            },
              {
                field: 'zipCode',
                column: 2,
                type: TYPE.TEXT
              },
              {
                field: 'town',
                column: 5,
                type: TYPE.TEXT
              },
              {
                field: 'stateAddress',
                column: 5,
                type: TYPE.TEXT
              },
            ]
        },
        {
          group: 'invoice_detail',
          fields: [
            {
              field: 'currency',
              column: 5,
              type: TYPE.DROPDOWN,
              filter: true,
              options: () => this.currencies,
              icon: id => 'cc-ico',
              iconValue: id => {
                const curr = this.currencies && this.currencies.find(value => value.id === id) ?
                  this.currencies.find(value => value.id === id) : undefined;
                if (curr)
                  return curr.value;
                return '';
              }
            },
            {
              field: 'billingDueNbDays',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              field: 'bankAccountId',
              column: 5,
              type: TYPE.DROPDOWN,
              options: () => this.bankAccounts
            },
            {
              field: 'invoiceMail',
              column: 12,
              type: TYPE.TEXT
            },
            {
              field: 'vat',
              column: 3,
              type: TYPE.CHECKBOX
            },
            {
              field: 'vatNumber',
              column: 3,
              type: TYPE.TEXT
            },
            {
              field: 'brokerageTableName',
              column: 3,
              type: TYPE.DROPDOWN,
              options: () => this.brokerageTablesVisibles
            },
            {
              field: 'invoiceTemplate',
              column: 3,
              type: TYPE.DROPDOWN,
              options: () => this.invoices
            },
          ]
        });
    }
  }

  onFormShow() {
    this.refreshBrokerageTables(this.formRecord);
  }

  protected initData() {
    super.initData();

    this.settingsFormLoadingProgressValue = 0;
    const callCount = 7;
    this.commonFieldService.getCountries(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.countries = fieldResult.records ? [...this.getFields(fieldResult)] : [];
      this.settingsFormLoadingProgressValue += 100 / callCount;
    })
    this.commonFieldService.getLanguages(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.languages = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / callCount;
    })
    this.commonFieldService.getOwners(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.owners = this.getFields(fieldResult);
      if (this.owners && this.owners.length > 0) {
        this.newRecord.owner = this.owners[0].text;
        if (this.formRecord) {
          this.formRecord.owner = this.owners[0].text;
          this.refreshBrokerageTables(this.formRecord);
        }
      }
      this.settingsFormLoadingProgressValue += 100 / callCount;
    })
    this.commonFieldService.getCurrencies(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.settingsFormLoadingProgressValue += 100 / callCount;
      this.currencies = fieldResult.records ? [...this.getFields(fieldResult)] : [];
    });
    this.commonFieldService.getBankAccounts(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.settingsFormLoadingProgressValue += 100 / callCount;
      this.bankAccounts = fieldResult.records ? [...this.getFields(fieldResult)] : [];
    });
    this.commonFieldService.getBrokerageTables(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.settingsFormLoadingProgressValue += 100 /callCount;
      this.brokerageTables = fieldResult.records ? [...this.getFields(fieldResult)] : [];
    });
    this.commonFieldService.getInvoices(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.settingsFormLoadingProgressValue += 100 / callCount;
      this.invoices = fieldResult.records ? [...this.getFields(fieldResult)] : [];
    });
  }

  private refreshBrokerageTables(record: ExecTrader) {
    this.brokerageTablesVisibles = this.brokerageTables.filter(value => value.value.ownerCode === record.owner)
      .filter((value, index, self) =>
        index === self.findIndex((t) => t.value.brokerageTableName === value.value.brokerageTableName)
      );
  }

  private enableRecord(record: ExecTrader) {
    this.formRecord = record;
    if (this.formRecord) {
      this.formRecord.enabled = !this.formRecord.enabled;
      this.save();
    }
  }
}
